import { forwardRef, useEffect, useState } from 'react';

import { Button, type ButtonProps } from '@mui/material';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { Icon } from '../Icon';
import styles from './SaveButton.module.scss';
import { uiIcons } from '@/constants/uiIcons';

export interface SaveButtonProps extends Omit<
  ButtonProps,
  'loading' | 'children'
> {
  loading: boolean;
  isSuccess?: boolean;
  maintainSuccessStateInButtonUntilTimeout?: boolean;
  label?: string;
}

export const SaveButton = forwardRef<HTMLButtonElement, SaveButtonProps>(
  (
    {
      loading = false,
      isSuccess = false,
      maintainSuccessStateInButtonUntilTimeout = true,
      label,
      variant = 'contained',
      color = 'secondary',
      disabled,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation('ui');
    const [internalSuccess, setInternalSuccess] = useState(false);

    useEffect(() => {
      if (isSuccess) {
        setInternalSuccess(true);

        const timer = setTimeout(() => {
          setInternalSuccess(false);
        }, 3500);

        return () => clearTimeout(timer);
      }
    }, [isSuccess]);

    const displaySuccess = maintainSuccessStateInButtonUntilTimeout
      ? internalSuccess
      : isSuccess;
    const isDisabled = disabled || loading;

    const getStartIcon = () => {
      if (displaySuccess) {
        return <Icon name={uiIcons.status.confirm} />;
      }
      return undefined;
    };

    const getButtonText = () => {
      if (loading) return `${t('saveButton.SavingLabel')}...`;
      if (displaySuccess) return t('saveButton.SavingSuccessLabel');
      return label ?? t('saveButton.DefaultLabel');
    };

    return (
      <Button
        ref={ref}
        {...props}
        className={cx(
          displaySuccess && styles.SaveButtonSuccess,
          props.className,
        )}
        disabled={isDisabled}
        loading={loading}
        loadingPosition="start"
        startIcon={getStartIcon()}
        variant={variant}
        color={displaySuccess ? undefined : color}
      >
        {getButtonText()}
      </Button>
    );
  },
);

SaveButton.displayName = 'SaveButton';
